import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatDate } from '../utils/dateUtils';

const LabelBox = ({ label, children, color = '#d32f2f', sx = {} }) => (
  <Box sx={{ position: 'relative', mt: 1.5, mb: 0.5, display: 'inline-block', ...sx }}>
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        top: -8,
        left: 8,
        bgcolor: 'background.paper',
        px: 0.5,
        color: color,
        fontSize: '0.65rem',
        fontWeight: 900,
        zIndex: 1,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        border: `1px solid ${color}`,
        borderRadius: 1.5,
        px: 1.2,
        py: 0.5,
        width: 'fit-content',
        minWidth: 60,
        display: 'flex',
        alignItems: 'center',
        bgcolor: color === '#d32f2f' ? 'rgba(211, 47, 47, 0.02)' : 'transparent'
      }}
    >
      {children}
    </Box>
  </Box>
);

const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }

  return String(value);
};

const PatientCard = ({ patient, onView, onEdit, onDelete, onNavigate }) => {
  const hasNavigate = Boolean(onNavigate);
  const fullName = formatValue(patient.__displayName);
  const patientId = formatValue(
    patient.id ??
      patient.__recordId ??
      patient.ID_PT ??
      patient.id_pt ??
      patient.__internalId ??
      null
  );
  const phone = formatValue(patient.__phone ?? patient.phone);
  const age = formatValue(patient.__age);
  const dateOfBirth = formatDate(patient.__dateOfBirth ?? patient.date_of_birth);
  const gender = formatValue(patient.__gender ?? patient.gender);
  const email = formatValue(patient.__email ?? patient.email);
  const address = formatValue(patient.__address ?? patient.address);
  const notes = formatValue(patient.__notes ?? patient.notes);
  const lastVisit = formatDate(patient.__lastVisit ?? patient.last_visit);
  const cardDate = formatDate(patient.__cardDate ?? patient.card_date ?? patient.datekarta ?? patient.__createdAt ?? patient.created_at);
  const updatedAt = formatDate(patient.__updatedAt ?? patient.updated_at);
  const notesPreview = formatValue(patient.__notes ?? patient.notes);
  const amka = formatValue(patient.__amka ?? patient.amka);
  const am = formatValue(patient.__am ?? patient.am);
  const diagnosis = formatValue(patient.__diagnosis ?? patient.diagnosis);
  const allergies = formatValue(patient.allergies);
  const death = formatValue(patient.death ?? patient.__death);

  return (
    <Card
      variant="outlined"
      sx={{ height: '100%', cursor: hasNavigate ? 'pointer' : 'default' }}
      onClick={() => {
        if (hasNavigate) {
          onNavigate?.();
        } else {
          onView?.();
        }
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <PersonIcon color="primary" />
            <Typography 
              variant="h6" 
              fontWeight={600}
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.25rem' },
                wordBreak: 'break-word',
                lineHeight: 1.2,
                display: 'inline-block'
              }}
            >
              {fullName}
            </Typography>
            {patientId !== '—' && (
              <Typography variant="subtitle2" color="text.secondary">
                ID: {patientId}
              </Typography>
            )}
          </Stack>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {amka !== '—' && (
                <LabelBox label="AMKA">
                  <Typography variant="body2">{amka}</Typography>
                </LabelBox>
              )}
              {age !== '—' && (
                <LabelBox label="Ηλικία">
                  <Typography variant="body2">{age}</Typography>
                </LabelBox>
              )}
              {dateOfBirth !== '—' && (
                <LabelBox label="Ημ/νία γέννησης" color="black">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">{dateOfBirth}</Typography>
                  </Stack>
                </LabelBox>
              )}
              {gender !== '—' && (
                <LabelBox label="Φύλο" color="black" sx={{ minWidth: 40 }}>
                  <Typography variant="body2">{gender}</Typography>
                </LabelBox>
              )}
              {cardDate !== '—' && (
                <LabelBox label="Δημιουργήθηκε">
                  <Typography variant="body2">{cardDate}</Typography>
                </LabelBox>
              )}
              {phone !== '—' && (
                <LabelBox label="Τηλέφωνο">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{phone}</Typography>
                  </Stack>
                </LabelBox>
              )}
            </Box>

            {diagnosis !== '—' && (
              <LabelBox label="Διάγνωση" sx={{ width: '100%', mt: 1 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', width: '100%' }}>{diagnosis}</Typography>
              </LabelBox>
            )}

            <Divider flexItem sx={{ my: 0.5, opacity: 0.1 }} />
            <Typography variant="body2">Email: {email}</Typography>
            <Typography variant="body2">Διεύθυνση: {address}</Typography>
            <Typography variant="body2">Σημειώσεις: {notes}</Typography>
            <Typography variant="body2">Τελευταία επίσκεψη: {lastVisit}</Typography>
            <Typography variant="body2">AM: {am}</Typography>
            <Typography variant="body2">Αλλεργίες: {allergies}</Typography>
            <Typography variant="body2">Θάνατος: {death}</Typography>
            <Typography variant="body2">Ενημερώθηκε: {updatedAt}</Typography>
          </Stack>
          <Divider flexItem sx={{ my: 1 }} />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            Σημειώσεις: {notesPreview}
          </Typography>
        </Stack>
      </CardContent>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Tooltip title={onView ? 'Quick view' : 'View details'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onView) {
                onView();
              } else {
                onNavigate?.();
              }
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(patient);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
};

export default PatientCard;
